/**
 * Process organization signature by replacing placeholders with actual user/org data
 */
export interface SignatureUserProfile {
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
  email?: string | null;
  contact_email?: string | null;
  address?: string | null;
  avatar_url?: string | null;
}

export interface SignatureOrganization {
  name: string;
  logo_url?: string | null;
  color?: string | null;
}

import { SUPABASE_STORAGE_URL } from '@/lib/supabaseConfig';

/**
 * Replace or remove placeholders based on whether value exists
 */
const replaceOrRemove = (html: string, pattern: RegExp, value: string | undefined | null): string => {
  if (value && value.trim()) {
    return html.replace(pattern, value);
  }
  // Remove the placeholder if value is empty
  return html.replace(pattern, '');
};

/**
 * Process a signature template by replacing all placeholders with actual values
 */
export function processSignature(
  signatureHtml: string,
  userProfile?: SignatureUserProfile | null,
  organization?: SignatureOrganization | null
): string {
  let processedSignature = signatureHtml;

  // Replace user placeholders
  if (userProfile) {
    processedSignature = replaceOrRemove(processedSignature, /\{\{vorname\}\}/g, userProfile.first_name);
    processedSignature = replaceOrRemove(processedSignature, /\{\{nachname\}\}/g, userProfile.last_name);
    processedSignature = replaceOrRemove(processedSignature, /\{\{telefon\}\}/g, userProfile.phone);
    processedSignature = replaceOrRemove(processedSignature, /\{\{email\}\}/g, userProfile.contact_email || userProfile.email);
    processedSignature = replaceOrRemove(processedSignature, /\{\{adresse\}\}/g, userProfile.address);

    // Handle advisor image with size - {{beraterbild:SIZE}}
    if (userProfile.avatar_url) {
      const avatarUrl = userProfile.avatar_url.startsWith('http')
        ? userProfile.avatar_url
        : `${SUPABASE_STORAGE_URL}/profile-avatars/${userProfile.avatar_url}`;

      processedSignature = processedSignature.replace(
        /\{\{beraterbild:(\d+)\}\}/g,
        (_, size) => `<img src="${avatarUrl}" alt="Berater" style="width: ${size}px; height: ${size}px; border-radius: 50%; object-fit: cover;" />`
      );
    } else {
      // Remove advisor image placeholder if no avatar
      processedSignature = processedSignature.replace(/\{\{beraterbild:\d+\}\}/g, '');
    }
  } else {
    // Remove all user placeholders if no profile
    processedSignature = processedSignature
      .replace(/\{\{vorname\}\}/g, '')
      .replace(/\{\{nachname\}\}/g, '')
      .replace(/\{\{telefon\}\}/g, '')
      .replace(/\{\{email\}\}/g, '')
      .replace(/\{\{adresse\}\}/g, '')
      .replace(/\{\{beraterbild:\d+\}\}/g, '');
  }

  // Replace organization placeholders
  if (organization) {
    processedSignature = replaceOrRemove(processedSignature, /\{\{organisation\}\}/g, organization.name);

    // Handle logo with size - {{logo:SIZE}}
    if (organization.logo_url) {
      const logoUrl = organization.logo_url.startsWith('http')
        ? organization.logo_url
        : `${SUPABASE_STORAGE_URL}/organization-logos/${organization.logo_url}`;

      processedSignature = processedSignature.replace(
        /\{\{logo:(\d+)\}\}/g,
        (_, size) => `<img src="${logoUrl}" alt="${organization.name}" style="width: ${size}px; height: auto;" />`
      );
    } else {
      // Remove logo placeholder if no logo
      processedSignature = processedSignature.replace(/\{\{logo:\d+\}\}/g, '');
    }
  } else {
    processedSignature = processedSignature
      .replace(/\{\{organisation\}\}/g, '')
      .replace(/\{\{logo:\d+\}\}/g, '');
  }

  // Clean up leading/trailing br tags
  processedSignature = processedSignature
    .replace(/^(\s*<br\s*\/?>\s*)+/g, '')
    .replace(/(\s*<br\s*\/?>\s*)+$/g, '');

  return processedSignature;
}
